/**
 * Workflow Tracing Utilities
 *
 * Provides a simple tracing abstraction for workflow execution.
 * Can be extended with OpenTelemetry for production distributed tracing.
 *
 * @packageDocumentation
 */

/**
 * Span attributes that can be added to a span.
 */
export interface SpanAttributes {
  [key: string]: string | number | boolean | undefined;
}

/**
 * Span status indicating success or failure.
 */
export type SpanStatus = 'ok' | 'error';

/**
 * A span representing a unit of work in a workflow execution.
 */
export interface Span {
  /** Span name */
  name: string;
  /** Start timestamp in ISO format */
  startTime: string;
  /** End timestamp in ISO format */
  endTime?: string;
  /** Duration in milliseconds */
  durationMs?: number;
  /** Span status */
  status: SpanStatus;
  /** Span attributes */
  attributes: SpanAttributes;
  /** Child spans */
  children: Span[];
  /** Error message if status is 'error' */
  errorMessage?: string;
}

/**
 * Trace context for correlating spans across services.
 */
export interface TraceContext {
  /** Trace ID - unique identifier for the entire trace */
  traceId: string;
  /** Span ID - unique identifier for this span */
  spanId: string;
  /** Parent span ID - for creating parent-child relationships */
  parentSpanId?: string;
}

/**
 * Creates a new span for tracking a unit of work.
 *
 * @param name - Name of the span
 * @param attributes - Initial attributes
 * @returns Span object
 *
 * @example
 * ```typescript
 * const span = createSpan('workflow.execute', {
 *   'workflow.name': 'invoice-processor',
 *   'workflow.run_id': 'run_123'
 * });
 *
 * // ... do work ...
 *
 * endSpan(span, 'ok');
 * ```
 */
export function createSpan(name: string, attributes: SpanAttributes = {}): Span {
  return {
    name,
    startTime: new Date().toISOString(),
    status: 'ok',
    attributes,
    children: [],
  };
}

/**
 * Ends a span and calculates duration.
 *
 * @param span - The span to end
 * @param status - Final status
 * @param errorMessage - Error message if status is 'error'
 */
export function endSpan(
  span: Span,
  status: SpanStatus = 'ok',
  errorMessage?: string
): void {
  span.endTime = new Date().toISOString();
  span.durationMs = new Date(span.endTime).getTime() - new Date(span.startTime).getTime();
  span.status = status;
  if (errorMessage) {
    span.errorMessage = errorMessage;
  }
}

/**
 * Adds a child span to a parent span.
 *
 * @param parent - Parent span
 * @param child - Child span to add
 */
export function addChildSpan(parent: Span, child: Span): void {
  parent.children.push(child);
}

/**
 * Sets an attribute on a span.
 *
 * @param span - The span
 * @param key - Attribute key
 * @param value - Attribute value
 */
export function setSpanAttribute(
  span: Span,
  key: string,
  value: string | number | boolean | undefined
): void {
  span.attributes[key] = value;
}

/**
 * Generates a unique trace ID.
 *
 * @returns 32-character hex string
 */
export function generateTraceId(): string {
  const bytes = new Uint8Array(16);
  if (typeof crypto !== 'undefined' && crypto.getRandomValues) {
    crypto.getRandomValues(bytes);
  } else {
    // Fallback for environments without crypto
    for (let i = 0; i < 16; i++) {
      bytes[i] = Math.floor(Math.random() * 256);
    }
  }
  return Array.from(bytes)
    .map(b => b.toString(16).padStart(2, '0'))
    .join('');
}

/**
 * Generates a unique span ID.
 *
 * @returns 16-character hex string
 */
export function generateSpanId(): string {
  const bytes = new Uint8Array(8);
  if (typeof crypto !== 'undefined' && crypto.getRandomValues) {
    crypto.getRandomValues(bytes);
  } else {
    // Fallback for environments without crypto
    for (let i = 0; i < 8; i++) {
      bytes[i] = Math.floor(Math.random() * 256);
    }
  }
  return Array.from(bytes)
    .map(b => b.toString(16).padStart(2, '0'))
    .join('');
}

/**
 * Creates a new trace context.
 *
 * @param parentContext - Optional parent context for child spans
 * @returns New trace context
 */
export function createTraceContext(parentContext?: TraceContext): TraceContext {
  return {
    traceId: parentContext?.traceId || generateTraceId(),
    spanId: generateSpanId(),
    parentSpanId: parentContext?.spanId,
  };
}

/**
 * Formats trace context as W3C traceparent header.
 *
 * @param context - Trace context
 * @returns traceparent header value
 *
 * @example
 * ```typescript
 * const context = createTraceContext();
 * const traceparent = formatTraceparent(context);
 * // "00-<traceId>-<spanId>-01"
 * ```
 */
export function formatTraceparent(context: TraceContext): string {
  return `00-${context.traceId}-${context.spanId}-01`;
}

/**
 * Parses W3C traceparent header.
 *
 * @param traceparent - traceparent header value
 * @returns Trace context or undefined if invalid
 */
export function parseTraceparent(traceparent: string): TraceContext | undefined {
  const parts = traceparent.split('-');
  if (parts.length !== 4 || parts[0] !== '00') {
    return undefined;
  }

  const [, traceId, spanId] = parts;

  if (traceId.length !== 32 || spanId.length !== 16) {
    return undefined;
  }

  return {
    traceId,
    spanId,
    parentSpanId: spanId, // Use the parsed span as parent
  };
}

/**
 * Converts a span tree to a flat array for logging/export.
 *
 * @param span - Root span
 * @returns Flat array of spans
 */
export function flattenSpans(span: Span): Span[] {
  const result: Span[] = [span];
  for (const child of span.children) {
    result.push(...flattenSpans(child));
  }
  return result;
}

/**
 * Calculates total duration from a span tree.
 *
 * @param span - Root span
 * @returns Total duration in milliseconds
 */
export function totalDuration(span: Span): number {
  return span.durationMs || 0;
}

/**
 * Counts total spans in a tree.
 *
 * @param span - Root span
 * @returns Total number of spans
 */
export function countSpans(span: Span): number {
  let count = 1;
  for (const child of span.children) {
    count += countSpans(child);
  }
  return count;
}
